'use client';

import { createContext, useContext, useState } from 'react';

import { Toast, type TToastProps } from '@crehana/compass.ui';

type TContext = {
  renderToast: (props: Omit<TToastProps, 'open' | 'onClose'>) => void;
};

type TToastProviderProps = {
  children: React.ReactNode;
};

export const ToastContext = createContext<TContext | undefined>(undefined);

/**
 * This component is used to render a global Toast Component by just calling the renderToast function.
 * Useful to do not need to import the compass.ui/Toast component every time you want to show a Toast
 * How to use?
 * 1. Import the ToastProvider and use it in the layout.tsx file to wrap all the content/components where you want to show the toast.
 * 2. Import and use the react hook `useToastContext` which returns a `renderToast` that you can use to show the toast.
 *
 * Example:
 * ````tsx
 * import { useToastContext } from '@/shared/contexts/Toast';
 *
 * const MyComponent = () => {
 *   const { renderToast } = useToastContext()
 *
 *   const onSubmit = () => {
 *     saveData();
 *     renderToast({
 *       type: 'success',
 *       title: 'Success!!',
 *       subtitle: 'Congrats!'
 *      })
 *   }
 *
 *   return (<div>...</div>)
 * }
 * ````
 */
export const ToastProvider: React.FC<TToastProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [toastProps, setToastProps] = useState<
    Omit<TToastProps, 'open' | 'onClose'> | undefined
  >(undefined);

  const renderToast: TContext['renderToast'] = props => {
    setToastProps(props);
    setIsOpen(true);
  };

  return (
    <ToastContext.Provider value={{ renderToast }}>
      {toastProps ? (
        <Toast {...toastProps} open={isOpen} onClose={() => setIsOpen(false)} />
      ) : null}
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used with in a ToastProvider.');
  }
  return context;
};
